import React, { useMemo } from 'react';

/**
 * Detects http(s) URLs and bare hostnames (e.g. exemplo.com.br).
 * Trailing sentence punctuation is kept outside the link where possible.
 */
const AUTOLINK_REGEX =
  /\b(https?:\/\/[^\s<.,;:!?)\]'"]+|(?:www\.)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s<]*)?)\b/gi;

function trimTrailingPunctuation(raw: string): string {
  let end = raw.length;
  while (end > 0 && '.,;:!?)'.includes(raw[end - 1]!)) end--;
  return end === raw.length ? raw : raw.slice(0, end);
}

function toSafeHref(displayUrl: string): string | null {
  const base = trimTrailingPunctuation(displayUrl);
  const candidate = /^https?:\/\//i.test(base) ? base : `https://${base}`;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
}

export type TextWithAutolinksProps = {
  text: string;
  className?: string;
};

/**
 * Renders plain text with URLs turned into external links (noopener).
 */
export function TextWithAutolinks({ text, className }: Readonly<TextWithAutolinksProps>) {
  const content = useMemo(() => {
    const nodes: React.ReactNode[] = [];
    const re = new RegExp(AUTOLINK_REGEX.source, AUTOLINK_REGEX.flags);
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }
      const full = match[0];
      const display = trimTrailingPunctuation(full);
      const href = toSafeHref(full);
      if (!href) {
        nodes.push(full);
      } else {
        nodes.push(
          <a
            key={`link-${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-accent underline underline-offset-2 hover:text-accent/90"
          >
            {display}
          </a>,
        );
      }
      lastIndex = match.index + full.length;
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes;
  }, [text]);

  return (
    <p className={className} role="note">
      {content}
    </p>
  );
}
